import CancelIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText, IconButton,
  InputAdornment,
  TextField
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import { useState } from "react";
import { fetchEmployee } from "../../api/employee";
import { getUserDataFromEmployeeService } from "../../util/employeeDataRetriever";
import { validateEmployee, validateEmployeeId } from "../../util/inputValidation";
import { INVALID_EMPLOYEE_ID } from "../../util/messageConstants";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import { useDispatch } from "react-redux";
import { getProxyUserSession } from "../../api/userAccess";
import { logIn } from "../../slices/userSession/userSessionSlice";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    fontWeight: "bold",
    marginLeft: "15px",
  },
  button: {
    margin: "5px",
    width: "100px",
  },
}));

const ProxyLoginDialog = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [employeeId, setEmployeeId] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");
  const [employeeIdSearchLoaded, setEmployeeIdSearchLoaded] = useState(false);
  const [employeeIdSearchTriggered, setEmployeeIdSearchTriggered] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isProxyLogInLoading, setIsProxyLogInLoading] = useState(false);

  const handleEmployeeIdChange = (eid) => {
    if (employeeData) {
      setEmployeeData(null);
    }
    setEmployeeId(eid.target.value.toLowerCase());
  };

  const handleEmployeeIdBlur = (event) => {
    let trimEmpId = event.target.value.trim();
    let errors = validateEmployeeId(trimEmpId);
    if (employeeIdErrors === "" || errors !== "") {
      setEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (!employeeData || trimEmpId !== employeeData.eid) {
        fetchEmployeeData(trimEmpId);
      }
    }
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const CancelButtonEmployeeId = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleEmployeeDataClear}>
        <CancelIcon />
      </IconButton>
    );
  };

  const handleEmployeeDataClear = () => {
    setEmployeeId("");
    setEmployeeData(null);
    let errors = validateEmployeeId("");
    if (errors !== "") {
      setEmployeeIdErrors(errors);
    }
  };

  const fetchEmployeeData = (eid) => {
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(true);
    setEmployeeIdErrors("");
    setEmployeeData(null);
    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let employeeData = await getUserDataFromEmployeeService(result);
          let employeeDataErrors = "";
          if (employeeDataErrors === "") {
            employeeDataErrors = validateEmployee(
              employeeData.eid,
              employeeData.firstName,
              employeeData.lastName,
              employeeData.station,
              getWorkgroupGivenJobCode(employeeData.jobCode)
            );
          }
          if (employeeDataErrors !== "") {
            console.log("no error")
            setEmployeeIdErrors(employeeDataErrors);
            setEmployeeIdSearchLoaded(true);
          }
          console.log("error")
          setEmployeeData(employeeData);
          setEmployeeIdSearchLoaded(true);
        } else {
          console.log("result error")
          setEmployeeIdErrors(INVALID_EMPLOYEE_ID);
          setEmployeeIdSearchLoaded(true);
        }
      })
      .catch((err) => {
        console.log("err", err)
        setEmployeeIdSearchTriggered(false);
      });
  };

  const handleConfirmationNoButtonClicked = () => {
    setIsConfirmationDialogOpen(false);
  };

  const handleConfirmationYesButtonClicked = async () => {
    await invokeGetProxyUserSession();
  };

  const handleCancelButtonClick = () => {
    handleEmployeeDataClear();
    props.setIsDialogOpen(false);
  };

  const handleLogInButtonClicked = () => {
    setIsConfirmationDialogOpen(true);
  };

  const invokeGetProxyUserSession = async () => {
    setIsProxyLogInLoading(true);
    getProxyUserSession(employeeId)
      .then((res) => res.json())
      .then((result) => {
        dispatch(logIn(result.userSessionData));
        props.setCookieSessionActiveFunc();
      })
      .catch((err) => {
        setIsProxyLogInLoading(false);
      });
  };

  return (
    <>
    <Dialog
      className="detail-dialog"
      data-testid="proxy-login-dialog"
      fullWidth
      fullScreen={!props.isDesktopView}
      maxWidth="xs"
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      onClose={handleCancelButtonClick}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle className={classes.dialogTitle} id="alert-dialog-slide-title">
        Proxy Login
      </DialogTitle>
      <DialogContent>
        <DialogContentText component={"div"} id="alert-dialog-slide-description">
          <Box mt={1} mr={2} ml={2}>
            <FormControl fullWidth variant="outlined" error={!!employeeIdErrors}>
              <TextField
                className={classes.textInput}
                label="Employee ID"
                variant="outlined"
                value={employeeId}
                onChange={handleEmployeeIdChange}
                onBlur={handleEmployeeIdBlur}
                onKeyUp={handleEmployeeIdEnterKeyPressed}
                InputProps={{
                  endAdornment:
                    employeeId !== "" ? (
                      <InputAdornment position="start">
                        <CancelButtonEmployeeId />
                      </InputAdornment>
                    ) : (
                      ""
                    ),
                }}
                error={!!employeeIdErrors}
              />
              <FormHelperText>{employeeIdErrors}</FormHelperText>
            </FormControl>
          </Box>
        </DialogContentText>
        <Box className={classes.employeeInfoText} mt={1} mb={2} ml={2}>
          {employeeIdSearchTriggered ? (
            employeeIdSearchLoaded ? (
              employeeIdErrors === "" && employeeData ? (
                <>
                  <div>
                    <span className="bold-text">Name:</span> {employeeData.lastName + ", " + employeeData.firstName}
                  </div>
                  {employeeData.tempStation && (
                    <div>
                    <span className="bold-text">Temp Station:</span> {employeeData.tempStation}
                  </div>
                  )}
                  <div>
                    <span className="bold-text">Home Station:</span> {employeeData.homeStation}
                  </div>
                  <div>
                    <span className="bold-text">Workgroup:</span> {getWorkgroupGivenJobCode(employeeData.jobCode)}
                  </div>
                </>
              ) : (
                ""
              )
            ) : (
              <CircularProgress className={classes.employeeInfoLoading} />
            )
          ) : (
            ""
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <div>
          <Button className={classes.button} variant="outlined" color="primary" onClick={handleCancelButtonClick} >
            CANCEL
          </Button>
          <Button className={classes.button} variant="contained" color="primary" onClick={handleLogInButtonClicked} disabled={!!!employeeData}>
            LOG IN
          </Button>
        </div>
      </DialogActions>
    </Dialog>

    
    {isConfirmationDialogOpen && (
      <ConfirmationDialog
        isDialogOpen={isConfirmationDialogOpen}
        message={`Are you sure you want to log in as ${employeeId}?`}
        handleNoButtonClicked={handleConfirmationNoButtonClicked}
        handleYesButtonClicked={handleConfirmationYesButtonClicked}
        isYesLoading={isProxyLogInLoading}
        hasWarning={true}
        warningMessage="All actions will result in real transactions"
      />
    )}

    </>
  );
};

ProxyLoginDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  setIsDialogOpen: PropTypes.func.isRequired,
  setCookieSessionActiveFunc: PropTypes.func.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default ProxyLoginDialog;
